import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import {
    providerIdZodSchema,
    type ProviderId,
} from "@@/shared/providers/provider-id";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type { GPTModelType } from "./conversation";
import type { AiFredConversationId } from "./conversation-id";
import { AiFredConversationIdZodSchema } from "./conversation-id";

type ConversationCommand = {
    readonly _id: string;
    readonly _type: "CONVERSATION_COMMAND";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly conversationId: AiFredConversationId;
    readonly providerId?: ProviderId;
    readonly systemPrompt?: string;
    readonly model?: GPTModelType;
    readonly newPrompt: { message: string; functions: string[] };

    readonly numberOfResults: number;
};

type FactoryParams = Except<
    ConversationCommand,
    "_id" | "_type" | "_createdAt"
>;
const conversationCommandFactory = (params: FactoryParams) => {
    const ConversationCommand: ConversationCommand = {
        ...params,
        _id: generateId({ prefix: "CONVERSATION_COMMAND__" }),
        _type: "CONVERSATION_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return ConversationCommand;
};

const conversationCommandZodSchema: z.ZodSchema<ConversationCommand> = z.object(
    {
        _id: idZodSchema,
        _type: z.literal("CONVERSATION_COMMAND"),
        _createdAt: isoAndUnixZodSchema,
        conversationId: AiFredConversationIdZodSchema,
        providerId: providerIdZodSchema.optional(),
        newPrompt: z.object({
            message: z.string(),
            functions: z.array(z.string()),
        }),
        systemPrompt: z.string().optional(),
        numberOfResults: z.number(),
        model: z.union([
            z.literal("gpt-4o"),
            z.literal("gpt-4o-mini"),
            z.literal("o3-mini"),
            z.literal("dall-e-3"),
            z.literal("dall-e-2"),
        ]),
    },
);

export { conversationCommandFactory, conversationCommandZodSchema };
export type {
    ConversationCommand,
    FactoryParams as conversationCommandFactoryParams,
};
